<template>
  <Teleport to="body">
    <Transition name="ui-confirm-fade">
      <div v-if="isVisible" class="ui-confirm">
        <div class="ui-confirm__backdrop" @click="closeOnBackdropClick && close()"></div>
        <div
          class="ui-confirm__content"
          :class="[`ui-confirm--${size}`, `ui-confirm--${type}`]"
          @keydown.capture="handleDialogKeydown"
        >
          <div class="ui-confirm__header">
            <!-- <div class="ui-confirm__icon" v-if="showIcon">
            <Icon :name="iconName" />
          </div> -->
            <h3 class="ui-confirm__title">
              <slot name="title">{{ title }}</slot>
            </h3>
          </div>
          <div class="ui-confirm__body">
            <slot>
              <div v-html="message"></div>
            </slot>
          </div>
          <div class="ui-confirm__footer" :class="{ 'ui-confirm__footer--with-hint': footerHint }">
            <p v-if="footerHint" class="ui-confirm__footer-hint" v-html="footerHint"></p>
            <div class="ui-confirm__footer-actions">
              <slot name="footer">
                <template v-if="confirmButtonFirst">
                  <UiButton v-if="showConfirmButton" ref="confirmButtonRef" :variant="confirmButtonVariant" :size="buttonSize" @click="handleConfirm">
                    {{ confirmButtonText }}
                  </UiButton>
                  <UiButton v-if="showCancelButton" :variant="cancelButtonVariant" :size="buttonSize" @click="handleCancel">
                    {{ cancelButtonText }}
                  </UiButton>
                </template>
                <template v-else>
                  <UiButton v-if="showCancelButton" :variant="cancelButtonVariant" :size="buttonSize" @click="handleCancel">
                    {{ cancelButtonText }}
                  </UiButton>
                  <UiButton v-if="showConfirmButton" ref="confirmButtonRef" :variant="confirmButtonVariant" :size="buttonSize" @click="handleConfirm">
                    {{ confirmButtonText }}
                  </UiButton>
                </template>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useModalStore } from '~/composables/useModalStore';

/**
 * UiConfirm 컴포넌트
 *
 * 사용자에게 확인/취소 선택을 요청하는 모달 대화상자 컴포넌트입니다.
 * 다양한 타입(정보, 성공, 경고, 에러)과 크기를 지원합니다.
 */
const props = defineProps({
  // 모달 표시 여부 (v-model)
  modelValue: {
    type: Boolean,
    default: false,
  },
  // 모달 타입 (info, success, warning, error)
  type: {
    type: String,
    default: 'info',
    validator: value => ['info', 'success', 'warning', 'error'].includes(value),
  },
  // 모달 크기 (small, medium, large)
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large'].includes(value),
  },
  // 모달 제목
  title: {
    type: String,
    default: '확인',
  },
  // 모달 메시지
  message: {
    type: String,
    default: '계속 진행하시겠습니까?',
  },
  // 아이콘 표시 여부
  showIcon: {
    type: Boolean,
    default: true,
  },
  // 배경 클릭 시 닫기 여부
  closeOnBackdropClick: {
    type: Boolean,
    default: true,
  },
  // 확인 버튼 표시 여부
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  // 취소 버튼 표시 여부
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  // 확인 버튼 텍스트
  confirmButtonText: {
    type: String,
    default: '확인',
  },
  // 취소 버튼 텍스트
  cancelButtonText: {
    type: String,
    default: '취소',
  },
  // 확인 버튼 스타일 변형
  confirmButtonVariant: {
    type: String,
    default: 'primary',
    validator: value => ['primary', 'secondary', 'primary-line', 'secondary-line'].includes(value),
  },
  // 취소 버튼 스타일 변형
  cancelButtonVariant: {
    type: String,
    default: 'secondary-line',
    validator: value => ['primary', 'secondary', 'primary-line', 'secondary-line'].includes(value),
  },
  // 푸터 왼쪽(버튼 앞) 보조 안내 문구
  footerHint: {
    type: String,
    default: '',
  },
  /** true이면 Enter/Space 키로 버튼이 눌리지 않음 (마우스 클릭만 허용) */
  mouseOnly: {
    type: Boolean,
    default: false,
  },
  /** true이면 확인 버튼을 취소 버튼 왼쪽에 배치 */
  confirmButtonFirst: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);

// 확인 버튼 ref
const confirmButtonRef = ref(null);

// 애니메이션을 위한 내부 상태
const isVisible = ref(false);

// 각 모달 표시 세션을 구분하기 위한 generation 값
// (confirm A를 닫기 위해 예약된 타이머가 confirm B까지 닫지 않도록 방지)
let openGeneration = 0;

// 모달 스택 관리
const { pushModalStack, popModalStack, isTopModal } = useModalStore();
const confirmUID = `confirm-${Date.now()}-${Math.random()}`;

// ESC로 닫기 위한 콜백 함수
const closeModal = () => {
  if (isVisible.value) {
    // eslint-disable-next-line no-console
    console.log('[UiConfirm] closeModal (ESC) called, isVisible:', isVisible.value);
    close();
  }
};

// modelValue 변경 감지
watch(
  () => props.modelValue,
  async newValue => {
    if (newValue) {
      // 새로 열릴 때마다 generation 증가
      openGeneration += 1;
      // eslint-disable-next-line no-console
      console.log('[UiConfirm] modelValue changed to true, openGeneration:', openGeneration);
      isVisible.value = true;
      // 모달 스택에 추가
      await nextTick();
      pushModalStack(confirmUID, closeModal);
      // 확인 버튼에 포커스 (mouseOnly일 때는 키보드 실수 방지를 위해 포커스하지 않음)
      if (props.showConfirmButton && !props.mouseOnly) {
        await nextTick();
        if (confirmButtonRef.value) {
          confirmButtonRef.value.$el?.focus();
        }
      }
    } else {
      // 애니메이션 후 닫기
      isVisible.value = false;
      // 모달 스택에서 제거
      popModalStack(confirmUID);
    }
  },
  { immediate: true },
);

// 닫기 함수
const close = () => {
  // eslint-disable-next-line no-console
  console.log('[UiConfirm] close() called. isVisible:', isVisible.value, 'modelValue:', props.modelValue);
  isVisible.value = false;
  // 현재 세션의 generation을 캡처
  const myGeneration = openGeneration;
  // 애니메이션 완료 후 modelValue 업데이트
  setTimeout(() => {
    // confirm이 다시 열려 openGeneration이 변경된 경우, 이전 세션의 close 타이머는 무시
    if (myGeneration === openGeneration) {
      // eslint-disable-next-line no-console
      console.log('[UiConfirm] emit update:modelValue(false), generation:', myGeneration);
      emit('update:modelValue', false);
    } else {
      // eslint-disable-next-line no-console
      console.log(
        '[UiConfirm] skip emit update:modelValue(false) because generation changed',
        'myGeneration:',
        myGeneration,
        'current:',
        openGeneration,
      );
    }
  }, 200); // 애니메이션 지속 시간과 동일
};

// 버튼 크기 계산
const buttonSize = computed(() => {
  switch (props.size) {
    case 'small':
      return 'small';
    case 'large':
      return 'large';
    case 'medium':
    default:
      return 'medium';
  }
});

// 알림 타입에 따른 아이콘 이름 계산
const iconName = computed(() => {
  switch (props.type) {
    case 'success':
      return 'check-circle';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'info':
    default:
      return 'info';
  }
});

// 확인 버튼 클릭 핸들러
const handleConfirm = () => {
  // eslint-disable-next-line no-console
  console.log('[UiConfirm] handleConfirm clicked');
  emit('confirm');
  close();
};

// 취소 버튼 클릭 핸들러
const handleCancel = () => {
  // eslint-disable-next-line no-console
  console.log('[UiConfirm] handleCancel clicked');
  emit('cancel');
  close();
};

// mouseOnly: Enter/Space 키로 버튼이 눌리지 않도록 차단
const handleDialogKeydown = event => {
  if (!props.mouseOnly) return;
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    event.stopPropagation();
  }
};
</script>

<style lang="scss" scoped>
.ui-confirm {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;

  // 배경 딤 효과
  &__backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
  }

  // 컨텐츠 영역
  &__content {
    position: relative;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 90%;
    width: 400px;
    display: flex;
    flex-direction: column;
    z-index: 1001;
    overflow: hidden;
  }

  // 헤더 영역
  &__header {
    display: flex;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid $border-color;
  }

  // 아이콘 스타일
  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    font-size: 24px;
    flex-shrink: 0;
  }

  // 제목 스타일
  &__title {
    @include font-style($body-large-bold);
    margin: 0;
    flex: 1;
  }

  // 본문 영역
  &__body {
    padding: 20px;
    @include font-style($body-medium-bold);
    color: $text-default-color;
    line-height: 1.5;
    word-break: keep-all; // 한글 단어 중간 줄바꿈 방지 (어절 단위 줄바꿈)
  }

  // 푸터 영역
  &__footer {
    padding: 12px 20px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    &--with-hint {
      justify-content: space-between;
    }
  }

  &__footer-hint {
    margin: 0;
    flex: 1;
    min-width: 0;
    text-align: left;
    @include font-style($body-xsmall);
    color: $text-default-color;
    line-height: 1.5;

    :deep(.ui-confirm__footer-hint-accent) {
      @include font-style($body-xsmall-bold);
      color: $text-primary;
    }
  }

  &__footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
  }

  // 크기 변형
  &--small {
    width: 320px;

    .ui-confirm__header {
      padding: 12px 16px;
    }

    .ui-confirm__body {
      padding: 16px;
    }

    .ui-confirm__footer {
      padding: 8px 16px;
    }

    .ui-confirm__icon {
      font-size: 20px;
    }

    .ui-confirm__title {
      @include font-style($body-small-bold);
    }
  }

  &--large {
    width: 500px;

    .ui-confirm__header {
      padding: 20px 24px;
    }

    .ui-confirm__body {
      padding: 24px;
    }

    .ui-confirm__footer {
      padding: 16px 24px;
    }

    .ui-confirm__icon {
      font-size: 28px;
    }

    .ui-confirm__title {
      @include font-style($body-large-bold);
    }
  }

  // 타입 변형
  &--info {
    .ui-confirm__icon {
      color: $info-color;
    }
  }

  &--success {
    .ui-confirm__icon {
      color: $success-color;
    }
  }

  &--warning {
    .ui-confirm__icon {
      color: $system-yellow;
    }
  }

  &--error {
    .ui-confirm__icon {
      color: $danger-color;
    }
  }
}

// 애니메이션
@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scale-in {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

// Vue Transition 애니메이션
.ui-confirm-fade-enter-active,
.ui-confirm-fade-leave-active {
  transition: opacity 0.2s ease;

  .ui-confirm__backdrop {
    transition: opacity 0.2s ease;
  }

  .ui-confirm__content {
    transition:
      transform 0.2s ease,
      opacity 0.2s ease;
  }
}

.ui-confirm-fade-enter-from {
  opacity: 0;

  .ui-confirm__backdrop {
    opacity: 0;
  }

  .ui-confirm__content {
    transform: scale(0.9);
    opacity: 0;
  }
}

.ui-confirm-fade-leave-to {
  opacity: 0;

  .ui-confirm__backdrop {
    opacity: 0;
  }

  .ui-confirm__content {
    transform: scale(0.9);
    opacity: 0;
  }
}
</style>
